import errorReporter from '@app/errorReporter';
import { getNoMoreShowings } from 'api/AppointmentConfigController';
import { Button, message, theme, Icon } from 'components';
import { AddListingToTourModalContext } from 'contexts';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ListingSummaryProps from '../../../types/listingSummaryPropsType';
import { ContextPortalID, createContextPortal } from './hooks';
import MobileFriendlyTooltip from './MobileFriendlyTooltip';
import { StyledTooltipButtonWrapper } from './StyledComponents';
import { TOURS_DISABLED_SHOWING_STATUS } from './constants';

type AddListingToTourModalButtonProps = {
  listing: ListingSummaryProps;
  preBookingInformation?: string;
};

const AddListingToTourButton: React.FC<AddListingToTourModalButtonProps> =
  createContextPortal(
    ContextPortalID.ADD_LISTING_TO_TOUR_CONTEXT,
    ({ listing: listingFromListingPage, preBookingInformation }) => {
      const {
        setModalVisible,
        setListing,
        listing: listingFromUniversalSearch,
        modalVisible,
      } = useContext(AddListingToTourModalContext);
      const [noMoreShowings, setNoMoreShowings] = useState<boolean>(false);
      const [loading, setLoading] = useState<boolean>(true);
      const showingUnavailable = TOURS_DISABLED_SHOWING_STATUS.includes(
        listingFromListingPage.availability,
      );

      useEffect(() => () => {
          if (modalVisible) setModalVisible(false);
        }, []);

      useEffect(() => {
        (async () => {
          try {
            setLoading(true);
            setNoMoreShowings(
              await getNoMoreShowings(listingFromListingPage.id),
            );
            setLoading(false);
          } catch (err) {
            message.error('Error fetching listing config');
            errorReporter.error(
              `Failed to fetch noMoreShowings when initializing addToTour button: ${err}`,
            );
          }
        })();
      }, [listingFromListingPage]);

      const tooltipText = useMemo(() => {
        if (noMoreShowings) {
          return 'You cannot add to a tour if showings are turned off for this listing';
        }
        if (showingUnavailable) {
          return 'You cannot add this listing to a tour';
        }
        return '';
      }, [noMoreShowings, showingUnavailable]);

      return (
        !loading && (
          <MobileFriendlyTooltip
            title={tooltipText && <span>{tooltipText}</span>}
          >
            <StyledTooltipButtonWrapper>
              <StyledAddToTourButton
                disabled={noMoreShowings || showingUnavailable}
                type="default"
                onClick={() => {
                  if (listingFromUniversalSearch) return;
                  const enhancedListing = {
                    ...listingFromListingPage,
                    preBookingInformation,
                  };
                  setListing(enhancedListing);
                  setModalVisible(true);
                }}
                data-cy="open-add-to-tour-modal-button"
              >
                <>
                  <Icon
                    width={12}
                    color={!noMoreShowings ? 'textPrimary' : 'disabledColor'}
                    type={'antdPlusOutlined'}
                  />{' '}
                  Add to Tour
                </>
              </StyledAddToTourButton>
            </StyledTooltipButtonWrapper>
          </MobileFriendlyTooltip>
        )
      );
    },
  );

export default AddListingToTourButton;

const StyledCreateNewTourButton = styled(Button)`
  &&& {
    height: 40px;
    width: 100%;
    border-radius: 2px;
    font-weight: normal;
    font-size: 16px;
    :not(:disabled):not(:hover) {
      color: ${theme.textColor};
    }
    ${Icon} {
      vertical-align: middle;
    }
  }
`;

const StyledAddToTourButton = styled(StyledCreateNewTourButton)`
  &&& {
    height: 34px;
    font-size: 14px;
    display: block;
  }
`;
